import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy, computed, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgxBarcode6Module } from 'ngx-barcode6';
import { NgxMaskDirective } from 'ngx-mask';
import { Subscription } from 'rxjs';
import { GlobalConstants } from 'src/app/common/global';
import { ModalNames } from 'src/app/common/modal.config';
import { ProfileSidebarComponent } from 'src/app/layout/profile-sidebar/profile-sidebar.component';
import { Services } from 'src/app/services/api.service';
import { ErrorMessageService } from 'src/app/services/error-message.util';
import { HelperFunctionsService } from 'src/app/services/helper-functions.service';
import { ModalService } from 'src/app/services/modal.service';
import { ProfileService } from 'src/app/services/profile.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { NotificationComponent } from '../notification/notification.component';
import { getNotificationConfig } from 'src/app/utils/notification-config.util';
import { EventsService } from 'src/app/services/getEvents';
import { EventsListComponent } from '../events-list/events-list.component';

interface MembershipCard  {
  id: number,
  validFrom: string,
  validTo: string,
  barcode: string,
  freeTickets: number,
  isActive: boolean,
  isRenewable: boolean,
  isVerified: boolean,
  firstName: string,
  lastName: string,
  city: string
}

@Component({
  selector: 'app-season-ticket-profile',
  templateUrl: `./${environment.pageFolder}/season-ticket-profile.${environment.pageFolder}.component.html`,
  styleUrls: [`./${environment.pageFolder}/season-ticket-profile.${environment.pageFolder}.component.sass`],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ProfileSidebarComponent, NgxBarcode6Module, NgxMaskDirective, TranslateModule, NotificationComponent, EventsListComponent]
})

export class SeasonTicketProfileComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();
  loader = document.getElementById("loader-container");
  errorMessage: string = "";
  infoMessage: string = "";
  seasonTickets: any = [];
  selectedGroupTicket: any;
  ticketPdf: string = "";
  url:any;
  mySeasonTicket: boolean = true;
  isLoading: boolean = true;
  connectSeasonTicketForm: FormGroup = this.formBuilder.group({
    dat_rodj: ['', Validators.required],
    barcode: ['', Validators.required]
  });
  hasSeasonTicket: boolean | undefined;
  hasOtherSeasonTicket: boolean | undefined;
  seasonTicketsDisplay: any;
  showConnectSeasonticket: boolean = false;
  showConnectSeasonticketContainer: boolean = false;
  pageFolderName: string = environment.page;
  public readonly modalNamesEnum = ModalNames;
  readonly isMembershipCardModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.MembershipCard) ?? false);
  showNotification: boolean = false;
  bodyText: string = '';
  areGroupTicketsLoaded: boolean = false;
  allEvents: any;
  @Output() barcodeSelected = new EventEmitter<string>();

  constructor(
    private http: HttpClient,
    private formBuilder: FormBuilder,
    private service: Services,
    private profileService: ProfileService,
    private readonly errorMessageService: ErrorMessageService,
    public modalService: ModalService,
    private helperFunctionsService: HelperFunctionsService,
    private readonly router: Router,
    private eventsService: EventsService
  ) {}

  ngOnInit(): void {
    this.showNotificationModal();
    this.getSeasonTickets(true);
    this.getEvents();
  }

  getSeasonTickets(initialState: boolean) {
    this.loader!.style.display = "flex";

    this.profileService.getSubsribersTickets(initialState).subscribe((Response: any) => {
      const response: any = Response;
      this.seasonTickets = response.data;

      this.seasonTicketsDisplay = [this.seasonTickets.own, ...(this.seasonTickets.others ?? [])].filter((item: any) => item !== null);
      this.hasSeasonTicket = response.data.own;

      this.showConnectSeasonticketContainer = true;
      this.hasOtherSeasonTicket = response.data.others?.length > 0;
      this.areGroupTicketsLoaded = true;
      this.isLoading = false;
      this.loader!.style.display = "none";
    }, (error: any) => {
      this.loader!.style.display = "none";
    })
  }

  goToBuySeasonTicket() {
    sessionStorage.setItem("membershipHolder", "others");
    document.location = `${environment.redirectUrl}/buy-membership`
  }

  getTicketInvoice(transID: string, transactionType:string) {
    this.loader!.style.display = "flex";

    this.http.get<any>(`${environment.apiUrl}/conn/${environment.connId}/v1/user/transactions/${transID}/${transactionType}`, GlobalConstants.headersCentral).subscribe({
      next: data => {
        let response:any = data;
        this.ticketPdf = "data:application/pdf;base64," + response.data;
        const byteArray = new Uint8Array(atob(response.data).split('').map(char => char.charCodeAt(0)));
        let blob = new Blob([byteArray], {type: 'application/pdf'});
        this.url = window.URL.createObjectURL(blob);
        window.open(this.url);

        let isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        if(isSafari) {
          const fileName = 'ulaznica.pdf';
          const downloadLink = document.createElement('a');
          downloadLink.href = this.url;
          downloadLink.download = fileName;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }

        this.loader!.style.display = "none";
      },
      error: () => {
        this.loader!.style.display = "none";
        this.errorMessage = '';
      }
    })
  }

  onSubmitConnectSeasonTicket() {
    this.errorMessage = "";
    this.infoMessage = "";

    this.connectSeasonTicketForm.markAllAsTouched();
    if (this.connectSeasonTicketForm.invalid) {
      const dateField = this.connectSeasonTicketForm.get('dat_rodj')!;
      const barcodeField = this.connectSeasonTicketForm.get('barcode')!;

      if (dateField.hasError('required') || barcodeField.hasError('required')) {
        this.errorMessage = this.errorMessageService.getErrorMessage('allFieldsRequired');
      }

      return;
    }

    this.loader!.style.display = "flex";

    const body = {
      birthDate: this.connectSeasonTicketForm.value.dat_rodj.trim(),
      barcode: this.connectSeasonTicketForm.value.barcode.toString().trim()
    };

    this.mySeasonTicket = false;
    const connectionType = this.mySeasonTicket ? "" : "/others";

    this.subscription = this.service.connectSeasonTicketService(body, connectionType).subscribe({
      next: (data: any) => {
        if(data.data.status) {
          this.infoMessage = this.errorMessageService.getSuccessMessage("connectedSuccess");
          this.getSeasonTickets(false);
          this.connectSeasonTicketForm.reset();
          this.helperFunctionsService.forceReloadTransferTickets = true;
        } else if(data.data.message === "ime_i_prezime_ne_odgovara_profilu") {
          this.errorMessage = this.errorMessageService.getErrorMessage("seasonTicketNameMismatch");
          this.loader!.style.display = "none";
        }  else if(data.data.message === "vec_dodano") {
          this.errorMessage = this.errorMessageService.getErrorMessage("seasonTicketAlreadyAdded");
          this.loader!.style.display = "none";
        } else if(data.data.message === "kombinacija_barkod_datum_rodjenja_ne_postoji") {
          this.errorMessage = this.errorMessageService.getErrorMessage("seasonTicketAndBirthDateDoesntMatch");
          this.loader!.style.display = "none";
        } else if(data.data.message === "ime_i_prezime_su_isti") {
          this.errorMessage = this.errorMessageService.getErrorMessage("seasonTicketNameAndProfileMatch");
          this.loader!.style.display = "none";
        } else {
          this.errorMessage = this.errorMessageService.getErrorMessage("connectingError");
          this.loader!.style.display = "none";
        }
      },
      error: (error: any) => {
        this.errorMessage = this.errorMessageService.getErrorMessage("connectingError");
        this.loader!.style.display = "none";
      }
    });
  }

  changeSeasonTicket(isMyMembership: boolean) {
    this.mySeasonTicket = isMyMembership;
    this.seasonTicketsDisplay = [];
    this.errorMessage = "";
    this.infoMessage = "";
    this.seasonTicketsDisplay = this.mySeasonTicket ? [this.seasonTickets.own] : this.seasonTickets.others;
  }

  getTransactionInPdf(transID: string, transactionType: string) {
    this.profileService.getTransactionAndOpenPdf(transID, transactionType, this.loader!);
  }

  openGroupTicketModal(groupTicket: any) {
    this.selectedGroupTicket = groupTicket;
    this.modalService.openModal(this.modalNamesEnum.MembershipCard);
    // Emit barcode when button is clicked
    if (groupTicket.barcode) {
      this.barcodeSelected.emit(groupTicket.barcode);
    }
  }

  limitInputLengthForNumberic(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    let inputValue = inputEl.value.replace(/\D/g, '');
    if (inputValue.length > 12) {
      inputValue= inputValue.slice(0, 12);
    }
    inputEl.value = inputValue;
  }

  private showNotificationModal(): void {
    const notificationConfig = getNotificationConfig(this.router.url, 'PACKAGES');
    this.showNotification = notificationConfig.show;
    this.bodyText = notificationConfig.bodyTextKey;
  }

  getEvents() {
    this.subscription.add(
      this.eventsService.getEvents().subscribe((response: any) => {
        this.allEvents = response.data;
        this.allEvents = this.allEvents.filter((event: any) => event.isVisible !== false && event.actions?.length > 0 && event.isGroup);

        this.areGroupTicketsLoaded && (this.loader!.style.display = "none");
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
